import {isNil, isUndefined} from 'lodash';
import React, {FC, Ref, useMemo} from 'react';
import styled from 'styled-components';
import {Key} from 'ts-key-enum';

import {useLocalize} from '../../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {defineMessage} from '../../../../../../libs/shared/core/src/helpers/internationalization/message';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {KeyboardEventHandler} from '../../../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {BoxedInput} from '../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {BoxedInputChildProps} from '../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputConstants';
import {
  BoxedInputElement,
  BoxedInputElementTypesEnum,
} from '../../../../../../libs/shared/ui/src/components/boxedInputs/boxedInputElement';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {Loader, LoaderVariantsEnum} from '../../../../../../libs/shared/ui/src/components/loaders/loader';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {ConversationSearchContextProps} from '../highlights/conversationSearchContext';

/*
 * Props.
 */

interface ConversationSearchBarProps {
  onCloseClick: () => void;
  conversationSearchContext: ConversationSearchContextProps;

  inputRef?: Ref<BoxedInput>;
}

/*
 * Style.
 */

const StyledSearchDiv = styled.div`
  width: 210px;
`;

const StyledAccessoriesDiv = styled.div`
  grid-area: icon;
  display: flex;
  align-items: center;
`;

const StyledCounterDiv = styled.span`
  font-size: ${fontSizes.tiny};
  font-weight: ${fontWeights.semibold};
  color: ${(p) => p.theme.greys.shade80};
  margin-right: 8px;
  line-height: 20px;
`;

const StyledControlsDiv = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-column-gap: 1px;

  margin: 4px;
  border-radius: 3px;
  overflow: hidden;
`;

const StyledControlButton = styled(ChromelessButton)`
  padding: 2px;
  background-color: ${(p) => (p.isDisabled ? p.theme.greys.shade40 : p.theme.alphas.black20)};
  color: ${(p) => (p.isDisabled ? p.theme.greys.shade70 : p.theme.greys.shade80)};

  &:hover {
    background: ${(p) => p.theme.alphas.black40};
  }
`;

/*
 * Component.
 */

export const ConversationHeaderSearchBar: FC<ConversationSearchBarProps> = (props) => {
  const {inputRef, onCloseClick, conversationSearchContext} = props;

  const localize = useLocalize();
  const theme = useUserTheme();
  const {
    query = '',
    results,
    matchesCount,
    focusedResultIndex,
    setQuery,
    goToPrevious,
    goToNext,
  } = conversationSearchContext;

  /** Focus the previous/next result on Enter. */
  const onKeyDown: KeyboardEventHandler = (event) => {
    // We only want enter and up/down arrow keys
    if (event.key !== Key.Enter && event.key !== Key.ArrowDown && event.key !== Key.ArrowUp) {
      return;
    }

    event.stopPropagation();
    event.preventDefault();

    if ((event.shiftKey && event.key === Key.Enter) || event.key === Key.ArrowUp) {
      goToPrevious();
      return;
    }

    goToNext();
  };

  const maybeRenderResultCounter = () => {
    // Check if we need to display a counter.
    if (!query || !results || isUndefined(focusedResultIndex)) {
      return null;
    }
    const count = matchesCount ?? results.length;
    if (count === -2) {
      return <Loader size={VisualSizesEnum.SMALL} variant={LoaderVariantsEnum.BASE} isEnabled />;
    }
    if (count === 0) {
      return (
        <StyledCounterDiv>
          <Trans id="conversation-header-search-bar-zero-results-counter">0 matches</Trans>
        </StyledCounterDiv>
      );
    }
    return (
      <StyledCounterDiv>
        {focusedResultIndex + 1}/{matchesCount}
      </StyledCounterDiv>
    );
  };

  const isSearchNavigationDisabled = useMemo(() => isNil(results) || results.length <= 1, [results]);

  const renderInputContent = (childProps: BoxedInputChildProps) => (
    <>
      <BoxedInputElement
        {...childProps}
        type={BoxedInputElementTypesEnum.TEXT}
        placeholder={localize(
          defineMessage({
            id: 'conversation-header-search',
            message: 'Find',
          }),
        )}
        value={query}
        onChange={setQuery}
        onKeyDown={onKeyDown}
      />
      <StyledAccessoriesDiv>
        {maybeRenderResultCounter()}
        <StyledControlsDiv>
          <StyledControlButton onClick={goToPrevious} isDisabled={isSearchNavigationDisabled}>
            <Icon name="chevronUpSmall" />
          </StyledControlButton>
          <StyledControlButton onClick={goToNext} isDisabled={isSearchNavigationDisabled}>
            <Icon name="chevronDownSmall" />
          </StyledControlButton>
          <StyledControlButton onClick={onCloseClick}>
            <Icon name="exitSmall" />
          </StyledControlButton>
        </StyledControlsDiv>
      </StyledAccessoriesDiv>
    </>
  );

  return (
    <StyledSearchDiv>
      <BoxedInput ref={inputRef} backgroundColor={theme.greys.shade30} shouldAutoFocus>
        {(childProps) => renderInputContent(childProps)}
      </BoxedInput>
    </StyledSearchDiv>
  );
};
